/**
 * Блокировка между вкладками одного браузера.
 *
 * Основной путь — Web Locks API (`navigator.locks`): браузер сам выдаёт
 * блокировку одной вкладке и снимает её, если вкладку закрыли. Запасной —
 * аренда в localStorage с опросом: она не строгая (запись в localStorage не
 * атомарна между процессами), но разводит вкладки во времени, а редкое
 * совпадение сервер переживает сам (grace period у обменянного токена).
 */

/** Дольше этого ждать блокировку не стоит: держатель завис — работаем без неё */
const WAIT_TIMEOUT_MS = 30_000
/** Срок аренды в localStorage: закрытая посреди работы вкладка не держит её вечно */
const LEASE_MS = 25_000
const POLL_MS = 100
const SETTLE_MS = 50
const LEASE_KEY_PREFIX = 'easya_lock:'

interface Lease {
  owner: string
  expires: number
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function readLease(key: string): Lease | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<Lease>
    if (typeof parsed.owner !== 'string' || typeof parsed.expires !== 'number') {
      return null
    }

    return { owner: parsed.owner, expires: parsed.expires }
  } catch {
    return null
  }
}

async function withStorageLease<T>(name: string, task: () => Promise<T>): Promise<T> {
  const key = `${LEASE_KEY_PREFIX}${name}`
  const owner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  const deadline = Date.now() + WAIT_TIMEOUT_MS
  let acquired = false

  try {
    while (!acquired && Date.now() < deadline) {
      const lease = readLease(key)
      if (lease === null || lease.expires < Date.now()) {
        localStorage.setItem(key, JSON.stringify({ owner, expires: Date.now() + LEASE_MS }))
        // Две вкладки могли записать аренду одновременно: остаётся последняя запись
        await sleep(SETTLE_MS)
        acquired = readLease(key)?.owner === owner
        if (acquired) {
          break
        }
      }
      await sleep(POLL_MS + Math.floor(Math.random() * POLL_MS))
    }
  } catch {
    // localStorage недоступен — выполняем без блокировки
  }

  try {
    return await task()
  } finally {
    if (acquired) {
      try {
        if (readLease(key)?.owner === owner) {
          localStorage.removeItem(key)
        }
      } catch {
        // см. выше
      }
    }
  }
}

/**
 * Выполнить задачу, пока другие вкладки этого браузера ждут ту же блокировку.
 * Задача выполняется всегда: не дождались блокировки — без неё.
 */
export async function withCrossTabLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks || typeof locks.request !== 'function') {
    return withStorageLease(name, task)
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), WAIT_TIMEOUT_MS)
  let started = false

  try {
    return await locks.request(name, { signal: controller.signal }, async () => {
      started = true
      clearTimeout(timer)

      return task()
    })
  } catch (error) {
    if (started) {
      throw error
    }

    // Не дождались (или Web Locks отказали) — задача важнее порядка
    return task()
  } finally {
    clearTimeout(timer)
  }
}
